import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  Truck, Lock, ArrowLeft, ArrowUp, ArrowDown, X, Pencil, Send, Undo2, Ban, AlertTriangle, Info, Plus, PackageCheck,
  FileText, CheckCircle2, ArrowRightLeft, Mail, FileSpreadsheet,
} from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz, getTodayInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { LoadStatusBadge } from '../../components/nursery/despatch/LoadStatusBadge';
import { LoadFields, type LoadFieldsValue } from '../../components/nursery/despatch/LoadFields';
import { despatchWhen } from '../../components/nursery/despatch/despatchText';
import { DespatchDialog } from '../../components/nursery/despatch/DespatchDialog';
import { LoadTransfersDialog } from '../../components/nursery/despatch/LoadTransfersDialog';
import { RouteMap } from '../../components/nursery/despatch/RouteMap';
import { NoticeDialog } from '../../components/nursery/despatch/NoticeDialog';
import { AddCustomersDialog } from '../../components/nursery/despatch/AddCustomersDialog';
import { SupplierManifestDialog } from '../../components/nursery/despatch/SupplierManifestDialog';
import { PickModeCard } from '../../components/nursery/despatch/PickModeCard';
import { downloadBlob } from '../../utils/downloadBlob';
import {
  type NurseryDeliveryZone,
  type NurseryLoad,
  type DespatchResult,
  type NoticeKind,
} from '../../types/nursery';
import type { ApiResponse } from '../../types/api';

/**
 * /nursery/loads/:id — one load: what stands between it and a clean pick,
 * its details, and its stops in delivery order.
 *
 * The checks are worked out by the server on every read (unallocated orders,
 * stock at a farm other than the despatch farm, nobody picking there, over
 * capacity), so what this page says is never stale.
 *
 * A load is picked from allocations, or by the pickers choosing the lots
 * (PickModeCard). On the second, nothing is allocated and the allocation
 * checks and chips do not apply.
 */

const EDITABLE = ['planning', 'released'];
// Customers can still join once picking has started; only removing and
// reordering stop then.
const ADDABLE = ['planning', 'released', 'picking', 'loaded'];

function LoadDetail() {
  const { t } = useTranslation('nursery', { keyPrefix: 'LoadDetail' });
  const { id } = useParams<{ id: string }>();
  const loadId = Number(id);
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [load, setLoad] = useState<NurseryLoad | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<LoadFieldsValue | null>(null);
  const [farms, setFarms] = useState<{ id: number; name: string; picker_count: number }[]>([]);
  const [zones, setZones] = useState<NurseryDeliveryZone[]>([]);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const { hasAccess: isAdmin } = useModuleLicense('nursery', 'admin');
  const [despatching, setDespatching] = useState(false);
  const [moving, setMoving] = useState(false);
  const [done, setDone] = useState<DespatchResult | null>(null);
  const [moved, setMoved] = useState<string[] | null>(null);
  const [docBusy, setDocBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<NoticeKind | null>(null);
  const [adding, setAdding] = useState(false);
  const [manifestOpen, setManifestOpen] = useState(false);

  const printDoc = async (doc: 'pick-list' | 'run-sheet' | 'dockets') => {
    setDocBusy(doc);
    setError(null);
    try {
      downloadBlob(await nurseryService.getLoadPdf(loadId, doc), `${doc}-${load?.load_number ?? loadId}.pdf`);
    } catch (e) {
      setError(errorMessage(e, t('docFailed')));
    } finally {
      setDocBusy(null);
    }
  };

  const fetchLoad = useCallback(async () => {
    setError(null);
    try {
      const res = await nurseryService.getLoad(loadId);
      if (res.success) setLoad(res.data);
      else setError(errorMessage(res, t('notFound')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [loadId, t]);

  useEffect(() => {
    if (canManage) fetchLoad();
  }, [canManage, fetchLoad]);

  /** Run an action that returns the updated load. */
  const act = async (fn: () => Promise<ApiResponse<NurseryLoad>>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res.success) setLoad(res.data);
      else setError(errorMessage(res, fallback));
      return res.success;
    } catch (e) {
      setError(errorMessage(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const startEdit = async () => {
    if (!load) return;
    try {
      const [pick, zoneRes] = await Promise.all([nurseryService.getPickers(), nurseryService.getDeliveryZones()]);
      if (pick.success) {
        setFarms(pick.data.farms.map((f) => ({
          ...f, picker_count: pick.data.pickers.filter((p) => p.farm_ids.includes(f.id)).length,
        })));
      }
      if (zoneRes.success) setZones(zoneRes.data || []);
    } catch { /* the form still works with what it has */ }
    setEditing({
      load_type: load.load_type,
      despatch_farm_id: load.despatch_farm_id,
      despatch_date: load.despatch_date,
      zone_ids: load.zone_ids,
      vehicle: load.vehicle,
      driver_name: load.driver_name,
      carrier_name: load.carrier_name,
      consignment_ref: load.consignment_ref,
      capacity_trees: load.capacity_trees,
      notes: load.notes,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { load_type: _type, ...rest } = editing;
    const ok = await act(() => nurseryService.updateLoad(loadId, rest), t('saveFailed'));
    if (ok) setEditing(null);
  };

  const move = (index: number, delta: number) => {
    if (!load?.orders) return;
    const ids = load.orders.map((o) => o.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + delta, 0, moved);
    act(() => nurseryService.reorderLoadStops(loadId, ids), t('reorderFailed'));
  };

  if (licenseLoading || (loading && canManage)) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('licenseTitle')}</h2>
          <p className="text-sm text-neutral-500 mb-4">
            <Trans t={t} i18nKey="licenseBody" components={{ b: <span className="font-medium" /> }} />
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('backToNursery')}
          </Button>
        </div>
      </div>
    );
  }

  if (!load) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-sm text-red-700 mb-4">{error || t('notFound')}</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/nursery/loads')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('backToLoads')}
        </Button>
      </div>
    );
  }

  const editable = EDITABLE.includes(load.status);
  const addable = ADDABLE.includes(load.status);
  const orders = load.orders ?? [];
  const checks = load.checks ?? [];
  const flagged = new Map<number, string[]>();
  for (const c of checks) for (const oid of c.order_ids ?? []) flagged.set(oid, [...(flagged.get(oid) ?? []), c.code]);
  const over = load.capacity_trees != null && load.total_trees > load.capacity_trees;
  const warnings = checks.filter((c) => c.severity === 'warning');
  const freePick = load.pick_mode === 'free';
  const typeLabel = t(`loadType.${load.load_type}`);

  return (
    <>
      <PageHeader
        title={t('title', { load: load.load_number })}
        subtitle={t('subtitle', {
          type: load.is_daily ? t('typeDaily', { type: typeLabel }) : typeLabel,
          date: formatLocalDateInTz(load.despatch_date, tz),
          farm: load.despatch_farm_name,
        })}
        icon={<Truck className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap items-center gap-2">
            <LoadStatusBadge status={load.status} />
            {load.status === 'planning' && (
              <Button size="sm" disabled={busy || !orders.length}
                onClick={() => {
                  if (warnings.length && !window.confirm(t('confirmRelease', { count: warnings.length }))) return;
                  act(() => nurseryService.releaseLoad(loadId), t('releaseFailed'));
                }}>
                <Send className="h-4 w-4" /> {t('release')}
              </Button>
            )}
            {load.status === 'loaded' && (
              <Button size="sm" onClick={() => setDespatching(true)}>
                <Truck className="h-4 w-4" /> {t('despatch')}
              </Button>
            )}
            {['released', 'picking', 'loaded'].includes(load.status) && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/nursery/picking/${loadId}`)}>
                <PackageCheck className="h-4 w-4" /> {t('pickList')}
              </Button>
            )}
            {load.status === 'released' && (
              <Button variant="outline" size="sm" disabled={busy}
                onClick={() => act(() => nurseryService.unreleaseLoad(loadId), t('unreleaseFailed'))}>
                <Undo2 className="h-4 w-4" /> {t('backToPlanning')}
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {done && (
          <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-900">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            <div className="min-w-0 space-y-1">
              <p className="font-semibold">{t('despatched', { count: done.movements })}</p>
              {(done.lots_short?.length ?? 0) > 0 && (
                <p className="break-words">
                  {t('countedShort')}{' '}
                  {done.lots_short!.map((l, i) => (
                    <span key={`${l.lot_id}-${l.order_number}`}>{i > 0 ? ', ' : ''}<Link to={`/nursery/lots/${l.lot_id}`} className="underline">{l.lot_code || t('lotFallback', { id: l.lot_id })}</Link>{t('countedShortLot', { showed: l.showed, picked: l.picked, order: l.order_number })}</span>
                  ))}
                  {t('countedShortEnd')}
                </p>
              )}
              {done.backorders.length > 0 && (
                <p className="break-words">
                  {t('backorders')}{' '}
                  {done.backorders.map((b, i) => (
                    <span key={b.order_id}>{i > 0 ? ', ' : ''}<Link to={`/nursery/orders/${b.order_id}`} className="underline">{b.order_number}</Link> ({b.trees})</span>
                  ))}
                </p>
              )}
              {(done.taken_off?.length ?? 0) > 0 && (
                <p className="break-words">
                  {t('takenOff')}{' '}
                  {done.taken_off!.map((o, i) => (
                    <span key={o.order_id}>{i > 0 ? ', ' : ''}<Link to={`/nursery/orders/${o.order_id}`} className="underline">{o.order_number}</Link>{o.customer_name ? ` (${o.customer_name})` : ''}</span>
                  ))}
                  {t('takenOffEnd', { count: done.taken_off!.length })}
                </p>
              )}
              {done.cancelled.length > 0 && <p className="break-words">{t('shortfallCancelled', { orders: done.cancelled.join(', ') })}</p>}
              {(done.invoices_authorised ?? 0) > 0 && (
                <p className="break-words">
                  {t('invoicesFinal', { count: done.invoices_authorised })}{' '}
                  <Link to="/accounts/invoices" className="underline">{t('invoicesLink')}</Link>
                </p>
              )}
              {(done.invoices_held ?? []).map((h) => (
                <p key={h.invoice_id} className="break-words text-amber-800">
                  {t('invoiceHeld', { count: h.lines.length, invoice: h.invoice_number, order: h.order_number, lines: h.lines.join(', ') })}{' '}
                  <Link to={`/accounts/invoices/${h.invoice_id}`} className="underline">{t('invoiceLink')}</Link>
                </p>
              ))}
              {(done.refunds ?? []).map((r) => (
                <p key={r.order_id} className={r.status === 'failed' ? 'break-words text-red-800' : 'break-words'}>
                  {r.status === 'succeeded'
                    ? t('refundSucceeded', { amount: r.amount.toFixed(2) })
                    : r.status === 'failed'
                      ? t('refundFailed', { amount: r.amount.toFixed(2), reason: r.error ?? t('noReason') })
                      : t('refundPending', { amount: r.amount.toFixed(2) })}{' '}
                  <Link to={`/nursery/orders/${r.order_id}`} className="underline">{t('orderLink')}</Link>
                </p>
              ))}
            </div>
          </div>
        )}
        {moved && (
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
            <ArrowRightLeft className="h-5 w-5 flex-shrink-0" />
            <span className="break-words">
              {t('transfersRaised', { count: moved.length, transfers: moved.join(', ') })}{' '}
              <Link to="/nursery/transfers" className="underline">{t('transfersLink')}</Link>
            </span>
          </div>
        )}

        {load.status !== 'cancelled' && load.status !== 'despatched' && (
          <PickModeCard load={load} editable={editable} onChanged={setLoad} />
        )}

        {/* Checks */}
        {checks.length > 0 && (
          <ul className="space-y-2" aria-label={t('checksLabel')}>
            {checks.map((c) => (
              <li key={c.code}
                className={cn('flex items-start gap-2 rounded-lg border px-3 py-2 text-sm',
                  c.severity === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-neutral-50 border-neutral-200 text-neutral-700')}>
                {c.severity === 'warning'
                  ? <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
                  : <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-neutral-500" />}
                <span className="min-w-0">
                  {c.message}
                  {c.code === 'NO_PICKER' && (
                    <> <Link to="/nursery/pickers" className="underline font-medium">{t('assignPickers')}</Link></>
                  )}
                  {c.code === 'NEEDS_TRANSFER' && load.status !== 'despatched' && load.status !== 'cancelled' && (
                    <> <button type="button" onClick={() => setMoving(true)} className="underline font-medium">{t('moveStock')}</button></>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Paperwork */}
        {load.status !== 'cancelled' && orders.length > 0 && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4" aria-labelledby="load-paperwork">
            <h2 id="load-paperwork" className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">{t('paperwork')}</h2>
            <div className="flex flex-wrap gap-2">
              {([
                ['pick-list', t('docPickList')],
                ['run-sheet', load.load_type === 'run' || load.load_type === 'ad_hoc' ? t('docRunSheet')
                  : load.load_type === 'cross_dock' ? t('docCrossDockSheet') : t('docOrderSheet')],
                ['dockets', t('docDockets')],
              ] as ['pick-list' | 'run-sheet' | 'dockets', string][]).map(([doc, label]) => (
                <Button key={doc} variant="outline" size="sm" disabled={docBusy !== null} onClick={() => printDoc(doc)}>
                  <FileText className="h-4 w-4" /> {docBusy === doc ? t('making') : label}
                </Button>
              ))}
              {load.load_type === 'cross_dock' && (
                <Button variant="outline" size="sm" disabled={docBusy !== null} onClick={() => setManifestOpen(true)}>
                  <FileSpreadsheet className="h-4 w-4" /> {t('supplierManifest')}
                </Button>
              )}
            </div>
            <p className="text-xs text-neutral-500 mt-2">
              {t('paperworkNote')}
              {load.load_type === 'cross_dock' && t('manifestNote')}
            </p>
          </section>
        )}

        {/* Customers */}
        {orders.length > 0 && load.status !== 'cancelled' && (() => {
          const despatched = load.status === 'despatched';
          const told = orders.filter((o) => despatched
            ? !!o.despatched_notice_at
            : o.scheduled_notice_date != null && o.scheduled_notice_date.slice(0, 10) === load.despatch_date.slice(0, 10)).length;
          return (
            <section className="bg-white border border-neutral-200 rounded-xl p-4" aria-labelledby="load-customers">
              <h2 id="load-customers" className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-2">{t('customers')}</h2>
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-neutral-700 flex-1 min-w-0">
                  {despatched
                    ? t('toldOnTheWay', { told, total: orders.length })
                    : t('toldAbout', { told, total: orders.length, date: formatLocalDateInTz(load.despatch_date, tz) })}
                </p>
                <Button variant="outline" size="sm" onClick={() => setNotice(despatched ? 'despatched' : 'scheduled')}>
                  <Mail className="h-4 w-4" /> {despatched ? t('tellOnTheWay') : t('tellCustomers')}
                </Button>
              </div>
            </section>
          );
        })()}

        {/* Route */}
        {(load.load_type === 'run' || load.load_type === 'ad_hoc') && orders.length > 0 && load.status !== 'cancelled' && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4" aria-labelledby="load-route">
            <h2 id="load-route" className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">{t('route')}</h2>
            <RouteMap
              orders={orders}
              farm={load.despatch_farm_point && (load.despatch_farm_point.lat || load.despatch_farm_point.lng)
                ? { lat: Number(load.despatch_farm_point.lat), lng: Number(load.despatch_farm_point.lng) } : null}
              farmName={load.despatch_farm_name}
              editable={editable}
              busy={busy}
              onApply={(ids) => act(() => nurseryService.reorderLoadStops(loadId, ids), t('reorderFailed'))}
            />
          </section>
        )}

        {/* Details */}
        <section className="bg-white border border-neutral-200 rounded-xl p-5" aria-labelledby="load-details">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 id="load-details" className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('details')}</h2>
            {editable && !editing && (
              <Button variant="outline" size="sm" onClick={startEdit}><Pencil className="h-4 w-4" /> {t('edit')}</Button>
            )}
          </div>
          {editing ? (
            <>
              <LoadFields value={editing} onChange={setEditing} farms={farms} zones={zones} typeLocked />
              <div className="flex flex-wrap justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setEditing(null)} disabled={busy}>{t('cancel')}</Button>
                <Button size="sm" onClick={saveEdit} disabled={busy}>{busy ? t('saving') : t('save')}</Button>
              </div>
            </>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Detail label={t('trees')}>
                <span className={cn(over && 'text-amber-700 font-semibold')}>
                  {load.capacity_trees
                    ? t('treesOfCapacity', { trees: load.total_trees.toLocaleString(), capacity: load.capacity_trees.toLocaleString() })
                    : load.total_trees.toLocaleString()}
                </span>
              </Detail>
              <Detail label={t('pickersAtFarm')}>{load.picker_count ?? 0}</Detail>
              {load.zone_names.length > 0 && <Detail label={t('zones')}>{load.zone_names.join(', ')}</Detail>}
              {load.vehicle && <Detail label={t('vehicle')}>{load.vehicle}</Detail>}
              {load.driver_name && <Detail label={t('driver')}>{load.driver_name}</Detail>}
              {load.carrier_name && <Detail label={t('carrier')}>{load.carrier_name}</Detail>}
              {load.consignment_ref && <Detail label={t('reference')}>{load.consignment_ref}</Detail>}
              {load.notes && <Detail label={t('notes')} wide>{load.notes}</Detail>}
            </dl>
          )}
        </section>

        {/* Stops */}
        <section className="bg-white border border-neutral-200 rounded-xl" aria-labelledby="load-stops">
          <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b border-neutral-100">
            <h2 id="load-stops" className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">
              {load.load_type === 'run' ? t('stopsHeading', { n: orders.length }) : t('ordersHeading', { n: orders.length })}
            </h2>
            {addable && (
              <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                <Plus className="h-4 w-4" /> {t('addCustomers')}
              </Button>
            )}
          </div>
          {orders.length === 0 ? (
            <p className="text-sm text-neutral-500 text-center py-8">
              <Trans t={t} i18nKey="noOrders" components={{ link: <Link to="/nursery/despatch" className="text-primary-600 hover:underline" /> }} />
            </p>
          ) : (
            <ol className="divide-y divide-neutral-100">
              {orders.map((o, i) => {
                const when = despatchWhen(o, tz);
                // Picked without allocating: nothing is allocated, so nothing is short of it.
                const short = !freePick && o.allocated_trees < o.total_trees;
                const offFarm = freePick ? [] : o.stock_farms
                  // A lot with no place is picked at the despatch farm (Rob, 2026-10-12).
                  .filter((f) => f.farm_id != null && f.farm_id !== load.despatch_farm_id);
                return (
                  <li key={o.id} className="flex items-start gap-3 px-5 py-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-semibold text-neutral-700">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <Link to={`/nursery/orders/${o.id}`} className="font-medium text-primary-700 hover:underline">{o.order_number}</Link>
                        <span className="text-neutral-800 break-words min-w-0">{o.customer_name}</span>
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>{t(`method.${o.despatch_method}`)}</span>
                        {o.delivery_zone_name && <span className="break-words min-w-0">{o.delivery_zone_name}</span>}
                        {when && <span>{when}</span>}
                      </div>
                      {o.despatch_method === 'cross_dock' && (
                        <p className="text-xs text-neutral-700 mt-1 break-words">
                          {[o.customer_code ? t('store', { code: o.customer_code }) : null,
                            o.xdock_route ? t('xdockRoute', { route: o.xdock_route }) : t('noRoute'),
                            o.xdock_del_seq ? t('delSeq', { seq: o.xdock_del_seq }) : null,
                            o.customer_po ? t('po', { po: o.customer_po }) : null].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {o.despatch_method !== 'collection' && o.despatch_method !== 'cross_dock' && o.delivery_address && (
                        <p className="text-xs text-neutral-600 mt-1 whitespace-pre-line break-words">{o.delivery_address}</p>
                      )}
                      {(short || offFarm.length > 0 || (o.unpriced_lines ?? 0) > 0) && (
                        <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
                          {(o.unpriced_lines ?? 0) > 0 && (
                            <span className="rounded-full bg-red-100 text-red-800 px-2 py-0.5">
                              {t('unpriced', { count: o.unpriced_lines })}
                            </span>
                          )}
                          {short && (
                            <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">
                              {t('allocatedOf', { allocated: o.allocated_trees.toLocaleString(), total: o.total_trees.toLocaleString() })}
                            </span>
                          )}
                          {offFarm.map((f) => (
                            <span key={f.farm_id ?? 'none'} className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 break-words">
                              {t('atFarm', { quantity: f.quantity.toLocaleString(), farm: f.farm_name ?? t('noKnownFarm') })}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={cn('text-sm font-semibold', flagged.has(o.id) ? 'text-amber-700' : 'text-neutral-800')}>
                        {o.total_trees.toLocaleString()}
                      </span>
                      {editable && (
                        <div className="flex gap-1">
                          <IconButton label={t('moveUp', { order: o.order_number })} disabled={busy || i === 0} onClick={() => move(i, -1)}>
                            <ArrowUp className="h-4 w-4" />
                          </IconButton>
                          <IconButton label={t('moveDown', { order: o.order_number })} disabled={busy || i === orders.length - 1} onClick={() => move(i, 1)}>
                            <ArrowDown className="h-4 w-4" />
                          </IconButton>
                          <IconButton label={t('takeOff', { order: o.order_number })} disabled={busy}
                            onClick={() => act(() => nurseryService.removeOrderFromLoad(loadId, o.id), t('takeOffFailed'))}>
                            <X className="h-4 w-4" />
                          </IconButton>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {editable && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {confirmCancel ? (
              <>
                <span className="text-sm text-neutral-700">{t('confirmCancel')}</span>
                <Button variant="outline" size="sm" onClick={() => setConfirmCancel(false)}>{t('keepIt')}</Button>
                <Button size="sm" className="bg-red-600 hover:bg-red-700" disabled={busy}
                  onClick={async () => {
                    const ok = await act(() => nurseryService.cancelLoad(loadId), t('cancelFailed'));
                    if (ok) setConfirmCancel(false);
                  }}>
                  {t('cancelLoad')}
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setConfirmCancel(true)}>
                <Ban className="h-4 w-4" /> {t('cancelLoad')}
              </Button>
            )}
          </div>
        )}
      </div>

      {despatching && (
        <DespatchDialog
          loadId={loadId}
          loadNumber={load.load_number}
          timezone={tz}
          isAdmin={isAdmin}
          onClose={() => setDespatching(false)}
          onDespatched={(r, tell) => { setDespatching(false); setDone(r); fetchLoad(); if (tell) setNotice('despatched'); }}
        />
      )}
      {adding && (
        <AddCustomersDialog
          load={load}
          timezone={tz}
          onClose={() => setAdding(false)}
          onAdded={(l) => { setAdding(false); setLoad(l); }}
        />
      )}
      {manifestOpen && <SupplierManifestDialog loadId={loadId} timezone={tz} onClose={() => setManifestOpen(false)} />}
      {notice && (
        <NoticeDialog
          loadId={loadId}
          kind={notice}
          timezone={tz}
          onClose={() => setNotice(null)}
          onSent={() => fetchLoad()}
        />
      )}
      {moving && (
        <LoadTransfersDialog
          loadId={loadId}
          despatchFarmId={load.despatch_farm_id}
          despatchFarmName={load.despatch_farm_name}
          defaultDate={getTodayInTz(tz)}
          onClose={() => setMoving(false)}
          onCreated={(ts) => { setMoving(false); setMoved(ts.map((t) => t.transfer_number)); fetchLoad(); }}
        />
      )}
    </>
  );
}

function Detail({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={cn('min-w-0', wide && 'sm:col-span-2')}>
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className="text-neutral-800 break-words whitespace-pre-line">{children}</dd>
    </div>
  );
}

function IconButton({ label, children, ...rest }: { label: string; children: ReactNode; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} title={label}
      className="p-1.5 rounded border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40"
      {...rest}>
      {children}
    </button>
  );
}

export default LoadDetail;
